import {openDB} from 'idb';
import {enumMemoryCache} from "../enumMemoryCache";
import {LocaleType} from "../../../core/localization/intertace";

const DB_NAME = 'EnumDatabase';
const STORE_NAME = 'enums';
const VERSION = 1;

const getEnumDb = async () => {
    return await openDB(DB_NAME, VERSION, {
        upgrade(db) {
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME);
            }
        },
    });
};

const buildEnumKey = (key: string, locale: LocaleType) => `${locale}__${key}`;

export const setEnumToDb = async (key: string, locale: LocaleType, data: any[]) => {
    const db = await getEnumDb();
    await db.put(STORE_NAME, data, buildEnumKey(key, locale));
    enumMemoryCache.set(key, data); // memory'e de yaz
};


export const getEnumFromDb = async (key: string, locale: LocaleType): Promise<any[] | undefined> => {
    const db = await getEnumDb();
    const result = await db.get(STORE_NAME, buildEnumKey(key, locale));
    if (result) {
        enumMemoryCache.set(key, result); // memory'e de yükle
    }
    return result;
};

export const loadEnumsToMemoryFromDb = async (locale: LocaleType): Promise<void> => {
    const db = await getEnumDb();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);

    const keys = await store.getAllKeys();

    const localePrefix = `${locale}__`;
    const filteredKeys = keys.filter(
        (k): k is string => typeof k === 'string' && k.startsWith(localePrefix)
    );

    const tasks = filteredKeys.map(async (fullKey) => {
        const [, key] = fullKey.split("__");
        const data = await store.get(fullKey);
        if (data) {
            enumMemoryCache.set(key, data);
        }
    });

    await Promise.all(tasks);
};

export const clearAllEnumsFromDb = async () => {
    const db = await getEnumDb();
    await db.clear(STORE_NAME);
    enumMemoryCache.clearAll();
};

export const clearEnumByKeyAndLocaleFromDb = async (key: string, locale: LocaleType) => {
    const db = await getEnumDb();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const fullKey = buildEnumKey(key,locale);
    await store.delete(fullKey);
    enumMemoryCache.clear(key);
    await tx.done;
};
export const clearEnumByKeyFromDb = async (key: string) => {
    const db = await getEnumDb();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const keys = await store.getAllKeys();

    for (const k of keys) {
        if (typeof k === 'string' && k.endsWith(`__${key}`)) {
            store.delete(k);
        }
    }

    enumMemoryCache.clear(key);
    await tx.done;
};
export const clearEnumsByLocaleFromDb = async (locale: LocaleType) => {
    const db = await getEnumDb();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const prefix = `${locale}__`;
    const range = IDBKeyRange.bound(prefix, prefix + '\uffff', false, false);
    let cursor = await store.openKeyCursor(range);

    while (cursor) {
        const fullKey = cursor.key as string;
        const [, enumKey] = fullKey.split('__');
        store.delete(fullKey);
        enumMemoryCache.clear(enumKey);
        cursor = await cursor.continue();
    }

    await tx.done;
};
export const hasEnumInDb = async (key: string, locale: LocaleType): Promise<boolean> => {
    const db = await getEnumDb();
    const fullKey = buildEnumKey(key, locale);
    const keys = await db.getAllKeys(STORE_NAME);
    return keys.includes(fullKey);
};

export const getAllEnumKeys = async (locale?: string): Promise<string[]> => {
    const db = await getEnumDb();
    const keys = await db.getAllKeys(STORE_NAME);
    return keys
        .filter((k): k is string => typeof k === 'string')
        .filter(k => !locale || k.endsWith(`_${locale}`));
};
